"use client";

import { useCallback, useMemo, useState } from "react";
import {
  type BulkSelectAction,
  AssetsSparkSurface,
  BulkActionsBarWrapper,
  ConnectionKebabInteractionOption,
  ManagedConnectionSource,
  PageSizeProvider,
  Pagination,
  useEmitConnectionKebabInteractionEvent,
  useEmitManagedConnectionCreateClickedEvent,
  usePageSize,
} from "@zapier/shared";
import {
  BetaDataTableProvider as TableProvider,
  useBetaDataTableContext,
} from "@zapier/design-system";
import {
  Connection,
  useBulkTestAuthentications,
  useQueryDomainFilterGuards,
  useQueryManagedGuards,
} from "@zapier/client";
import { parseDomainRejection } from "./useCreateAppConnection/parseDomainRejection";
import { DomainErrorBanner } from "./DomainErrorBanner";
import { useTranslations } from "@zapier/i18n";
import { useNotification } from "@zapier/utils";
import { ConnectionsHeader } from "./ConnectionsHeader";
import ConnectionsTable from "./ConnectionsTable/ConnectionsTable";
import { useConnectionsList } from "./useConnectionsList";
import { useConnectionModalStates } from "./useConnectionModalStates";
import {
  useShowEmptyConnectionsTable,
  EmptyConnectionsTable as EmptyConnectionsTableEnum,
} from "./ConnectionsTable/useShowEmptyConnectionsTable";
import { useCanCreateManagedConnection } from "./useCanCreateManagedConnection";
import { useShowFirstConnectionEmptyState } from "./ConnectionsTable/useShowFirstConnectionEmptyState";
import { ConnectionErrorCallbackPayload, ConnectionModal } from "./types";
import { ConnectionsBulkDeleteModal } from "./ConnectionsBulkDeleteModal";
import { BulkTestProvider, useBulkTestContext } from "./BulkTestContext";

// A restricted connection can't be tested even if the caller still has
// `permissions.test` — the app itself is blocked, not just this credential.
export const isTestableConnection = (
  connection: Pick<Connection, "permissions" | "isRestricted">,
): boolean => !!connection.permissions?.test && !connection.isRestricted;

// True if at least one connection in the selection genuinely lacks test
// permission (as opposed to being restricted) — used to decide whether the
// bulk-test tooltip should cite "no permission" or "app restricted."
export const hasUnpermittedConnection = (
  connections: Pick<Connection, "permissions">[],
): boolean => connections.some((c) => !c.permissions?.test);

function ConnectionsWithPageSize() {
  const t = useTranslations("connections-page");
  const tBulkDelete = useTranslations("connections-bulk-delete-modal");
  const tBulkTest = useTranslations("connections-bulk-test");
  const { size: pageSize } = usePageSize();
  const { isAppNonCompliant, isLoading: isGuardsLoading } =
    useQueryManagedGuards();
  const { getDomainRestriction } = useQueryDomainFilterGuards();
  const [domainError, setDomainError] = useState<
    { domain: string; allowedDomains: string[] } | undefined
  >();

  const handleConnectionError = useCallback(
    ({ appId, errorDescription }: ConnectionErrorCallbackPayload) => {
      const parsed = parseDomainRejection(errorDescription);
      if (!parsed) return; // fail closed — only domain rejections get a banner
      const restriction = getDomainRestriction(appId, "create");
      setDomainError({
        domain: parsed.domain,
        allowedDomains: restriction?.domains ?? [],
      });
    },
    [getDomainRestriction],
  );

  const {
    page,
    isLoading,
    isLoadingSecondary,
    isFetching,
    connections,
    isNonCompliantFilter,
    totalPages,
    totalCount,
    sortChange,
  } = useConnectionsList({
    pageSize,
  });

  const {
    currentConnection,
    currentConnectionModal,
    setCurrentConnection,
    setCurrentConnectionModal,
  } = useConnectionModalStates();

  const { emit } = useEmitConnectionKebabInteractionEvent();
  const { emit: emitManagedConnectionCreateClicked } =
    useEmitManagedConnectionCreateClickedEvent();
  const [managedConnectionSource, setManagedConnectionSource] = useState<
    ManagedConnectionSource | undefined
  >();

  const showFirstConnectionEmptyState = useShowFirstConnectionEmptyState();
  const { isLoading: isPermissionsLoading } = useCanCreateManagedConnection();
  // Single source of truth for "is this account empty" — uses totalCount so
  // a stale page param can't be mistaken for a truly empty account.
  const emptyTableType = useShowEmptyConnectionsTable({
    isLoading: isLoading || isFetching || isPermissionsLoading,
    hasNoConnections: connections.length === 0,
    totalCount,
  });
  // Replaces the whole page below the title; filters/pagination have
  // nothing to act on with zero connections.
  const isFirstConnectionEmptyState =
    emptyTableType === EmptyConnectionsTableEnum.NoOwnedConnections &&
    showFirstConnectionEmptyState;

  const handleSetAddConnectionModalOpen = () => {
    // Clear any stale domain-error banner from a prior attempt
    setDomainError(undefined);
    setCurrentConnectionModal(ConnectionModal.AddConnection);
    emit(ConnectionKebabInteractionOption.AddConnection);
  };

  const handleSetAddManagedConnectionModalOpen = (
    source: ManagedConnectionSource,
  ) => {
    setDomainError(undefined);
    setCurrentConnectionModal(ConnectionModal.AddManagedConnection);
    setManagedConnectionSource(source);
    emit(ConnectionKebabInteractionOption.AddManagedConnection);
    emitManagedConnectionCreateClicked(source);
  };

  const [bulkDeleteConnections, setBulkDeleteConnections] = useState<
    Connection[]
  >([]);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

  const { selectedRows } = useBetaDataTableContext();

  const selectableConnections = useMemo(
    () =>
      connections.map((c) => ({
        ...c,
        id: String(c.id),
      })),
    [connections],
  );

  const selectedConnectionObjects = useMemo(() => {
    if (selectedRows.length === 0) return [];
    const selectedIdSet = new Set(selectedRows);
    return connections.filter((c) => selectedIdSet.has(String(c.id)));
  }, [selectedRows, connections]);

  const hasNoDeletableSelection = useMemo(
    () =>
      selectedConnectionObjects.length > 0 &&
      selectedConnectionObjects.every((c) => !c.permissions?.delete),
    [selectedConnectionObjects],
  );

  const hasNoTestableSelection = useMemo(
    () =>
      selectedConnectionObjects.length > 0 &&
      selectedConnectionObjects.every((c) => !isTestableConnection(c)),
    [selectedConnectionObjects],
  );

  // Distinguishes *why* nothing is testable, so the toolbar tooltip names the
  // actual blocker instead of always blaming permissions. A selection with any
  // permission-lacking connection keeps the permission copy (still a true
  // cause); only an all-restricted selection gets the restricted-specific one.
  const hasUnpermittedTestSelection = useMemo(
    () => hasUnpermittedConnection(selectedConnectionObjects),
    [selectedConnectionObjects],
  );

  const { createNotification } = useNotification();
  const { bulkTestAuthentications, isLoading: isBulkTestLoading } =
    useBulkTestAuthentications();
  const { setBulkPending, setBulkResults } = useBulkTestContext()!;
  const { clearSelection } = useBetaDataTableContext();

  const handleBulkTest = useCallback(
    async (_selectedIds: string[]) => {
      const testableIds = selectedConnectionObjects
        .filter(isTestableConnection)
        .map((c) => c.id);

      if (testableIds.length === 0) return;

      setBulkPending(testableIds);

      try {
        const result = await bulkTestAuthentications({ ids: testableIds });

        setBulkResults(result.successfulIds, result.failedIds);

        if (result.failedIds.length === 0) {
          createNotification({
            message: tBulkTest("notifications.all-active", {
              count: result.successfulIds.length,
            }),
            type: "success",
          });
        } else if (result.successfulIds.length > 0) {
          createNotification({
            message: tBulkTest("notifications.partial-failure", {
              successful: result.successfulIds.length,
              failed: result.failedIds.length,
            }),
            type: "warning",
          });
        } else {
          createNotification({
            message: tBulkTest("notifications.all-failed", {
              count: result.failedIds.length,
            }),
            type: "error",
          });
        }
      } catch {
        setBulkResults([], testableIds);
        createNotification({
          message: tBulkTest("notifications.all-failed", {
            count: testableIds.length,
          }),
          type: "error",
        });
      }

      clearSelection();
    },
    [
      selectedConnectionObjects,
      setBulkPending,
      bulkTestAuthentications,
      setBulkResults,
      createNotification,
      tBulkTest,
      clearSelection,
    ],
  );

  const handleBulkDelete = useCallback(
    (_selectedIds: string[]) => {
      setBulkDeleteConnections(selectedConnectionObjects);
      setIsBulkDeleteOpen(true);
    },
    [selectedConnectionObjects],
  );

  const bulkSelectActions: BulkSelectAction[] = useMemo(
    () => [
      {
        icon: "actionCheckConnection",
        text: tBulkTest("buttons.test-action"),
        disabled: hasNoTestableSelection || isBulkTestLoading,
        tooltipText: hasNoTestableSelection
          ? tBulkTest("notifications.cannot-test", {
              hasUnpermittedConnection: hasUnpermittedTestSelection
                ? "true"
                : "false",
            })
          : undefined,
        onClick: handleBulkTest,
      },
      {
        icon: "actionTrash",
        text: tBulkDelete("buttons.delete-action"),
        disabled: hasNoDeletableSelection || isBulkTestLoading,
        tooltipText: hasNoDeletableSelection
          ? tBulkDelete("notifications.no-permission")
          : undefined,
        onClick: handleBulkDelete,
      },
    ],
    [
      hasNoTestableSelection,
      hasUnpermittedTestSelection,
      hasNoDeletableSelection,
      isBulkTestLoading,
      tBulkTest,
      tBulkDelete,
      handleBulkTest,
      handleBulkDelete,
    ],
  );

  return (
    <>
      {domainError && (
        <DomainErrorBanner
          domain={domainError.domain}
          allowedDomains={domainError.allowedDomains}
          onDismiss={() => setDomainError(undefined)}
        />
      )}
      <ConnectionsHeader
        onAddConnection={handleSetAddConnectionModalOpen}
        onAddManagedConnection={handleSetAddManagedConnectionModalOpen}
        hideFilters={isFirstConnectionEmptyState}
      />
      {/* Render once the initial load settles, so the surface shows above the
          empty state too. Hidden only during the initial load to avoid a flash;
          left mounted through background refetches to avoid layout shift. */}
      {!isLoading && (
        <AssetsSparkSurface
          surfaceId={process.env.NEXT_PUBLIC_SPARK_SURFACE_ID_CONNECTIONS}
        />
      )}
      <ConnectionsTable
        allowsSorting={true}
        ariaLabel={t("accessibility.table-label")}
        bulkSelectActions={bulkSelectActions}
        connections={connections}
        emptyTableType={emptyTableType}
        totalCount={totalCount}
        isNonCompliantFilter={isNonCompliantFilter}
        currentConnection={currentConnection}
        currentConnectionModal={currentConnectionModal}
        isAppNonCompliant={isAppNonCompliant}
        isLoading={isLoading || isFetching || isPermissionsLoading}
        isLoadingSecondary={isLoadingSecondary || isGuardsLoading}
        managedConnectionSource={managedConnectionSource}
        onAddConnection={handleSetAddConnectionModalOpen}
        onAddManagedConnection={() =>
          handleSetAddManagedConnectionModalOpen(
            ManagedConnectionSource.EmptyState,
          )
        }
        onConnectionError={handleConnectionError}
        onSortChange={sortChange}
        selectableEntities={selectableConnections}
        setCurrentConnection={setCurrentConnection}
        setCurrentConnectionModal={setCurrentConnectionModal}
        showCheckboxes={true}
      />
      <BulkActionsBarWrapper />
      {!isFirstConnectionEmptyState && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          isLoading={isLoading || isFetching}
        />
      )}
      <ConnectionsBulkDeleteModal
        connections={bulkDeleteConnections}
        isOpen={isBulkDeleteOpen}
        setIsOpen={setIsBulkDeleteOpen}
      />
    </>
  );
}

/**
 * Wrap this component in PageSizeProvider so that the context is available
 * to the Table and Pagination components above.
 */
export function Connections() {
  return (
    <TableProvider>
      <PageSizeProvider>
        <BulkTestProvider>
          <ConnectionsWithPageSize />
        </BulkTestProvider>
      </PageSizeProvider>
    </TableProvider>
  );
}
